import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2, Calendar, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import { CALENDAR_URL as CALENDAR_URL_STATIC } from "../config/features";

const INTERESSEN = [
  { key: "master",       title: "SAT MASTER",   sub: "Für meine eigene Transformation" },
  { key: "mentor",       title: "SAT MENTOR",   sub: "Für meinen Weg als Begleiter" },
  { key: "herzbewegung", title: "Herzbewegung", sub: "Ich möchte Teil der Bewegung werden" },
  { key: "orientierung", title: "Orientierung", sub: "Ich möchte zuerst SAT kennenlernen" },
];

const API = process.env.REACT_APP_BACKEND_URL;

export default function Erstgespraech() {
  const [interesse, setInteresse] = useState("");
  const [calendarUrl, setCalendarUrl] = useState(CALENDAR_URL_STATIC || "");
  const [form, setForm] = useState({
    name: "", email: "", telefon: "", nachricht: "", wunschtermin: "",
    datenschutz_ok: false, newsletter_opt_in: false,
  });
  const [state, setState] = useState({ loading: false, error: "", success: false, id: null });

  // Fetch Calendly URL from backend so it works on Prod even without frontend env var.
  // Hardcoded fallback ensures it also works if backend env var isn't set yet.
  useEffect(() => {
    fetch(`${API}/api/health`)
      .then((r) => r.json())
      .then((h) => {
        if (h?.calendly_url) setCalendarUrl(h.calendly_url);
        else if (!calendarUrl) setCalendarUrl("https://calendly.com/good-life-impuls/30min");
      })
      .catch(() => {
        if (!calendarUrl) setCalendarUrl("https://calendly.com/good-life-impuls/30min");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "", success: false, id: null });
    try {
      const res = await fetch(`${API}/api/leads/erstgespraech`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, interesse }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || "Fehler beim Senden");
      setState({ loading: false, error: "", success: true, id: data.id });
    } catch (err) {
      setState({ loading: false, error: err.message, success: false, id: null });
    }
  };

  const canSubmit = interesse && form.name.trim().length >= 2 && form.email.includes("@") && form.datenschutz_ok;

  if (state.success) {
    return (
      <div className="min-h-screen bg-[#081328] text-white pt-32 pb-24" data-testid="erstgespraech-success-page">
        <SEO title="Anfrage erhalten" description="Deine Erstgesprächs-Anfrage wurde erfolgreich übermittelt." path="/erstgespraech" noindex />
        <div className="container-sat max-w-2xl text-center">
          <CheckCircle2 size={56} className="text-[#D4AF37] mx-auto mb-8" />
          <h1 className="serif text-4xl md:text-5xl font-light leading-tight">
            Danke, wir haben deine Anfrage erhalten.
          </h1>
          <p className="serif italic text-white/70 text-xl mt-8 leading-relaxed">
            Innerhalb von 48 Stunden meldet sich jemand aus dem SAT-Team persönlich bei dir.
          </p>
          <p className="text-white/60 mt-6 text-[15px] leading-relaxed">
            Bis dahin darfst du atmen. Du musst nichts vorbereiten. Falls du eine Bestätigungs-E-Mail
            erwartet und nicht erhalten hast, prüfe bitte deinen Spam-Ordner.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/" className="btn-secondary" data-testid="erstgespraech-back-home">Zur Startseite</Link>
            <Link to="/sat-htc" className="btn-ghost" data-testid="erstgespraech-explore-htc">SAT-HTC entdecken</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#081328] text-white pt-32 pb-24" data-testid="erstgespraech-page">
      <SEO
        title="Kostenloses Erstgespräch"
        description="Vereinbare dein kostenloses Erstgespräch mit der SAT Akademie. Persönlich. Vertraulich. Ohne Verkaufsdruck."
        path="/erstgespraech"
      />
      <div className="container-sat max-w-4xl">
        <div className="text-center mb-16">
          <div className="overline mb-4">Dein nächster Schritt</div>
          <h1 className="serif text-4xl md:text-6xl font-light leading-tight">
            Kostenloses <span className="italic text-[#D4AF37]">Erstgespräch</span>.
          </h1>
          <p className="serif italic text-white/70 text-xl mt-8 max-w-2xl mx-auto leading-relaxed">
            Persönlich. Vertraulich. Ohne Verkaufsdruck.
          </p>
          <p className="text-white/60 mt-4 text-[15px] leading-relaxed max-w-xl mx-auto">
            Wir klären gemeinsam, ob und wie SAT für deinen Weg passt.
            Investition und Aufnahme werden im Gespräch transparent besprochen.
          </p>
        </div>

        {/* Interessens-Auswahl */}
        <div className="mb-10">
          <div className="overline mb-4 text-center">Wähle dein Anliegen</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {INTERESSEN.map((i) => (
              <button
                key={i.key}
                type="button"
                onClick={() => setInteresse(i.key)}
                className={`text-left rounded-2xl border p-5 transition
                  ${interesse === i.key
                    ? "border-[#D4AF37] bg-[#D4AF37]/[0.07] shadow-[0_0_0_1px_#D4AF37]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
                data-testid={`erstgespraech-interest-${i.key}`}
              >
                <div className="serif text-lg font-light">{i.title}</div>
                <div className="text-white/55 text-[13px] mt-1 leading-snug">{i.sub}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Optional Calendly */}
        {interesse && calendarUrl && (
          <div className="mb-8 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-6" data-testid="erstgespraech-calendar-panel">
            <div className="flex items-start gap-4">
              <Calendar size={22} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="flex-1">
                <div className="serif text-lg mb-1">Direkt einen Termin wählen</div>
                <p className="text-white/65 text-sm leading-relaxed mb-4">
                  Schneller Weg: wähle einen freien Slot in unserem Kalender.
                </p>
                <a
                  href={calendarUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-sm"
                  data-testid="erstgespraech-open-calendar"
                >
                  Kalender öffnen <ArrowRight size={14} />
                </a>
                <p className="text-white/40 text-xs mt-4 italic">
                  Alternativ kannst du unten das Anfrageformular ausfüllen.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Formular */}
        <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 lg:p-10 space-y-6" data-testid="erstgespraech-form">
          <div className="overline">Anfrageformular</div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-white/60 uppercase tracking-widest">Name*</span>
              <input required minLength={2} maxLength={80} type="text" value={form.name} onChange={upd("name")}
                     className="mt-2 w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
                     placeholder="Dein voller Name" data-testid="erstgespraech-name" />
            </label>
            <label className="block">
              <span className="text-xs text-white/60 uppercase tracking-widest">E-Mail*</span>
              <input required type="email" value={form.email} onChange={upd("email")}
                     className="mt-2 w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
                     placeholder="du@beispiel.com" data-testid="erstgespraech-email" />
            </label>
            <label className="block">
              <span className="text-xs text-white/60 uppercase tracking-widest">Telefon (optional)</span>
              <input type="tel" value={form.telefon} onChange={upd("telefon")} maxLength={40}
                     className="mt-2 w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
                     placeholder="+41 …" data-testid="erstgespraech-telefon" />
            </label>
            <label className="block">
              <span className="text-xs text-white/60 uppercase tracking-widest">Wunschtermin (optional)</span>
              <input type="text" value={form.wunschtermin} onChange={upd("wunschtermin")} maxLength={120}
                     className="mt-2 w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30"
                     placeholder="z.B. „Di-Do abends" data-testid="erstgespraech-wunschtermin" />
            </label>
          </div>

          <label className="block">
            <span className="text-xs text-white/60 uppercase tracking-widest">Deine Nachricht (optional)</span>
            <textarea rows={4} value={form.nachricht} onChange={upd("nachricht")} maxLength={2000}
                      className="mt-2 w-full rounded-lg bg-[#081328] border border-white/15 focus:border-[#D4AF37] outline-none px-4 py-3 text-white placeholder:text-white/30 resize-none"
                      placeholder="Kurz worum es dir geht — je klarer, desto besser vorbereitet können wir sein."
                      data-testid="erstgespraech-nachricht" />
          </label>

          {/* Einwilligungen — NIEMALS vorangekreuzt */}
          <div className="space-y-3 pt-2">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="erstgespraech-consent-privacy-wrap">
              <input type="checkbox" checked={form.datenschutz_ok} onChange={upd("datenschutz_ok")}
                     className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" data-testid="erstgespraech-consent-privacy" />
              <span className="text-[13px] text-white/70 leading-relaxed">
                Ich habe die <Link to="/datenschutz" className="text-[#D4AF37] hover:underline">Datenschutzerklärung</Link> gelesen
                und stimme der Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage zu. Ich kann diese Einwilligung jederzeit widerrufen. *
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="erstgespraech-consent-newsletter-wrap">
              <input type="checkbox" checked={form.newsletter_opt_in} onChange={upd("newsletter_opt_in")}
                     className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]" data-testid="erstgespraech-consent-newsletter" />
              <span className="text-[13px] text-white/65 leading-relaxed">
                Zusätzlich: Ich möchte gelegentliche Impulse und Neuigkeiten der SAT Akademie per E-Mail erhalten (jederzeit abbestellbar).
              </span>
            </label>
          </div>

          {state.error && (
            <div className="flex items-start gap-3 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-200" data-testid="erstgespraech-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div>{state.error}</div>
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || state.loading}
            className="btn-primary w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="erstgespraech-submit"
          >
            {state.loading ? (<><Loader2 size={16} className="animate-spin" /> Wird gesendet …</>) : (<>Anfrage senden <ArrowRight size={16} /></>)}
          </button>

          <p className="text-white/40 text-xs italic pt-2">
            Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
          </p>
        </form>

        <div className="mt-14 text-center">
          <div className="serif italic text-[#D4AF37]">„Love Is Transformation.&ldquo;</div>
        </div>
      </div>
    </div>
  );
}
